import { type Dispatch } from 'react';

import type { WeightUnit } from './state/loggerDraft';
import type { LoggerAction } from './state/loggerDraftTypes';

// SetCountRow ＋/− handlers, extracted from useTimelineLogger to keep that hook
// under the 500-line cap (LG-14 split). ＋ appends one set seeded from the
// previous one (seedNextSet's carry-forward), − drops the trailing set, and
// each handler clears the manual selection so the new binding follows.
//
// #387 (Antenore 20/10): they act on THIS exercise only, in a superset too.
// They used to fan out to every member (add_round_to_group, 2026-06-01) to
// keep the rounds symmetric, and that made a circuit unmanageable: one extra
// set on one member meant ＋ here and − on every other. A whole round for the
// group is «Add round» / «Remove round» in the superset menu (LoggerModals).
export function useSetCountHandlers(args: {
  activeEntryIdx: number;
  defaultUnit: WeightUnit;
  /** Trailing set index of the active entry. */
  lastSetIdx: number;
  canRemove: boolean;
  dispatch: Dispatch<LoggerAction>;
  setSelectedSetIdx: (idx: number | null) => void;
  followHoldInsert?: (entryIdx: number, setIdx: number) => void;
}) {
  const {
    activeEntryIdx,
    defaultUnit,
    lastSetIdx,
    canRemove,
    dispatch,
    setSelectedSetIdx,
    followHoldInsert,
  } = args;

  const onAddSet = () => {
    if (activeEntryIdx < 0) return;
    dispatch({ type: 'add_set', entryIdx: activeEntryIdx, defaultUnit });
    setSelectedSetIdx(null);
  };

  const onRemoveSet = () => {
    if (activeEntryIdx < 0 || !canRemove) return;
    dispatch({ type: 'remove_set', entryIdx: activeEntryIdx, setIdx: lastSetIdx });
    setSelectedSetIdx(null);
  };

  // LG-77 — insert a set BEFORE `setIdx`, from the row's own pointer.
  //
  // ⛔ It does NOT fan out to a superset's other members, and since #387
  // neither do ＋/−. This one puts an extra set in front of one exercise (a
  // warm-up on the bench, a preparation set before a heavy single), which is
  // exactly a thing that belongs to that exercise alone. Fanning it out would
  // invent sets on the other members that the athlete never asked for.
  const onInsertSetBefore = (setIdx: number) => {
    if (activeEntryIdx < 0) return;
    dispatch({
      type: 'insert_set_at',
      entryIdx: activeEntryIdx,
      setIdx,
      defaultUnit,
    });
    followHoldInsert?.(activeEntryIdx, setIdx);
    // Bind the set that was just inserted: it is at `setIdx` now, and it is
    // the one the athlete is about to fill in. The ＋/− handlers clear the
    // selection instead because they act on the tail, which the default
    // binding already reaches.
    setSelectedSetIdx(setIdx);
  };

  return { onAddSet, onRemoveSet, onInsertSetBefore };
}
